/**
 * The small pieces every page reuses.
 *
 * Ported from website-enterprise's file of the same name. What is here is the
 * vocabulary the pages are written against: a panel, a title, a footnote, and
 * the pair of notices every mutation reports through. Having one shape for "it
 * worked" and one for "it did not" is what stops sixteen pages inventing
 * sixteen of each.
 *
 * Two additions Learn needs and enterprise did not: a `Loading` line, because
 * this app reads far more than it writes and every page has a first frame with
 * nothing in it; and `Empty`, which takes the *next action* rather than a
 * definition of the feature — the fourth copy rule in CLAUDE.md.
 */

import type React from 'react';
import { ds } from '../ds';
import { RADIUS, SHADOW, display, monoPlain } from '../theme';

export const Panel = ({
  children,
  style
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <section
    style={{
      background: 'var(--surface-card)',
      borderRadius: RADIUS.panel,
      boxShadow: SHADOW.hairline,
      padding: 24,
      ...style
    }}
  >
    {children}
  </section>
);

/** A panel heading with room for one control on the right. */
export function PanelHead({
  title,
  meta,
  children
}: {
  title: string;
  meta?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap'
      }}
    >
      <div style={{ display: 'grid', gap: 3 }}>
        <h2 style={display(17)}>{title}</h2>
        {meta && <span style={monoPlain}>{meta}</span>}
      </div>
      {children}
    </div>
  );
}

/** `meta` is a fact — a count, a date, a state. Not a subtitle paraphrasing the
 *  heading above it, and not a paragraph. */
export function PageTitle({
  title,
  meta,
  children
}: {
  title: string;
  meta?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap'
      }}
    >
      <div style={{ display: 'grid', gap: 4 }}>
        <h1 style={display(28)}>{title}</h1>
        {meta && <p style={{ ...monoPlain, margin: 0 }}>{meta}</p>}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{children}</div>}
    </div>
  );
}

export const Footnote = ({ children }: { children: React.ReactNode }) => (
  <p style={{ ...monoPlain, margin: 0 }}>{children}</p>
);

/** A banner for a thrown error, so every page reports failure the same way
 *  rather than each inventing its own. Pass it the string `errorText` built. */
export function ErrorNote({ children }: { children: React.ReactNode }) {
  const { Icon } = ds();
  if (!children) return null;
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        background: 'var(--note-bad-soft)',
        color: 'var(--note-bad-ink)',
        borderRadius: RADIUS.card,
        padding: '10px 14px',
        fontSize: 13.5
      }}
    >
      <Icon name="AlertTriangle" size={16} />
      <span>{children}</span>
    </div>
  );
}

/** The counterpart to ErrorNote, for an action that worked. Same shape, so a
 *  form that can report both does not have two layouts. */
export function OkNote({ children }: { children: React.ReactNode }) {
  const { Icon } = ds();
  if (!children) return null;
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        background: 'var(--note-ok-soft)',
        color: 'var(--note-ok-ink)',
        borderRadius: RADIUS.card,
        padding: '10px 14px',
        fontSize: 13.5
      }}
    >
      <Icon name="Check" size={16} />
      <span>{children}</span>
    </div>
  );
}

/** The first frame of every page. One word, in the mono voice the rest of the
 *  metadata uses, rather than a spinner that outlives most loads. */
export const Loading = () => (
  <Panel>
    <Footnote>Loading…</Footnote>
  </Panel>
);

/**
 * Nothing here yet.
 *
 * `children` is the next thing to do, not a description of what the feature
 * would hold if it held anything. An empty state that explains the feature is
 * the fourth shape CLAUDE.md says to catch.
 */
export function Empty({ line, children }: { line: string; children?: React.ReactNode }) {
  return (
    <Panel style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
      <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-muted)' }}>{line}</p>
      {children}
    </Panel>
  );
}
